/**
 * format.ts — money and date display helpers.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: money is carried as integer PENCE across every GPS Clinics contract and
 * dates as true-UTC ISO strings. Rendering must never do ad-hoc arithmetic on
 * pence (rounding bugs) or show a raw timestamp. These helpers are the single
 * formatting truth so prices and dates read consistently everywhere.
 *
 * PRICE PLACEHOLDER RULE (CTO feed): a monthly price of 0 pence means "not yet
 * set" — callers render {@link PRICE_AT_LAUNCH} instead of "£0.00".
 */

import type { Address, PatientPaymentKind } from '../api/types';

/** Shown wherever a price is a 0-pence placeholder (pre-CTO-feed). */
export const PRICE_AT_LAUNCH = 'Price set at launch';

/**
 * One-line display form of a postal address, e.g.
 * "1 High Street, Flat 2, Leeds, LS1 1AA". `line2` is included only when
 * present (empty parts are dropped, never a stray ", ,").
 *
 * WHY: this is the SINGLE address formatter for the portal. A code-review audit
 * found this one line inlined in three places — ProfilePage, the checkout
 * summary and the admin payments panel — and they had already DRIFTED (the
 * checkout summary silently dropped `line2`). Rendering the address from one
 * helper is the only thing that keeps them from diverging again.
 */
export function formatAddress(address: Address): string {
  return [address.line1, address.line2, address.city, address.postcode].filter(Boolean).join(', ');
}

/**
 * A short human label for a bill's payment kind (contracts `PaymentKind`), for
 * the billing-history row. The dashboard bills carry `kind` (an enum), NOT a
 * free-text description — R19: the display copy is derived here, never sent.
 */
export function billKindLabel(kind: PatientPaymentKind): string {
  switch (kind) {
    case 'month1_preauth':
      return 'First month';
    case 'callback_49':
      return 'Callback booking';
    case 'renewal':
      return 'Monthly renewal';
    default:
      return 'Payment';
  }
}

/**
 * Format integer pence as GBP, e.g. 4999 → "£49.99", 5000 → "£50".
 * Whole pounds drop the ".00" for a cleaner headline price.
 */
export function formatPence(pence: number): string {
  const rounded = Math.round(pence);
  const pounds = rounded / 100;
  const hasFraction = rounded % 100 !== 0;
  return `£${pounds.toLocaleString('en-GB', {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * A monthly price line: the placeholder string when 0, otherwise "£x / month".
 */
export function formatMonthly(pence: number): string {
  return pence <= 0 ? PRICE_AT_LAUNCH : `${formatPence(pence)} / month`;
}

/**
 * A body weight in kilograms, e.g. `12` → "12 kg".
 *
 * Rounded to whole kilograms ON PURPOSE. These figures are averages from trial
 * data, and a decimal place implies a precision the underlying claim does not
 * have — "11.4 kg" reads as a measurement of this patient, "11 kg" reads as the
 * estimate it is. Negatives are clamped to 0: a projection is a loss, and a
 * sign error should show nothing rather than an absurdity.
 *
 * A SPACE BEFORE THE UNIT, like every other weight in the journey (journey
 * polish #7, 2026-09-24): the Result chart, the projection count-up and the
 * weight-declaration readback all print "98 kg", and the plan estimate printed
 * "8kg" beside them. It is a NO-BREAK space (U+00A0), so a figure never wraps
 * away from its unit at the end of a line.
 */
export const KG_UNIT_SEPARATOR = '\u00a0';

export function formatKg(kg: number): string {
  return `${Math.max(0, Math.round(kg))}${KG_UNIT_SEPARATOR}kg`;
}

/**
 * Format a UTC ISO timestamp as a UK long date, e.g. "5 August 2026".
 * Forces the UTC time zone so the displayed day never drifts by locale.
 * Returns '' for an empty/invalid input rather than "Invalid Date".
 */
export function formatUtcDate(iso: string | undefined | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

/**
 * Format a UTC ISO timestamp as a UK long date PLUS a 24-hour time, e.g.
 * "15 August 2026, 09:30". Built from {@link formatUtcDate} + a fixed `h23`
 * 24-hour clock (never a locale-dependent "24:00"/am-pm) so the rendered
 * string is stable and the TIME is always visible — the admin needs to see
 * WHEN a staff account last logged in/out, not just the day. Forces UTC so the
 * time never drifts by the viewer's locale. Returns '' for empty/invalid input.
 */
export function formatUtcDateTime(iso: string | undefined | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const time = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: 'UTC',
  }).format(date);
  return `${formatUtcDate(iso)}, ${time}`;
}

/**
 * Format an ISO instant as UK wall-clock time, 24-hour, e.g. "14:03". Returns
 * '' for an empty/invalid input.
 *
 * WHY EUROPE/LONDON HERE WHEN EVERYTHING ABOVE FORCES UTC. The UTC helpers are
 * right for a DAY that must not drift by locale. They are wrong for a DEADLINE
 * somebody acts on: the deal drawer tells staff when a card's pre-authorisation
 * stops being capturable, and from late March to late October the UTC clock
 * reads that deadline an hour early. The time a desk compares it with is the
 * one on the wall.
 */
export function formatLondonTime(iso: string | undefined | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: 'Europe/London',
  }).format(date);
}

/**
 * The same instant as a UK long date plus 24-hour time, both in Europe/London,
 * e.g. "18 September 2026, 14:03". The DATE is London's too: between 23:00 and
 * midnight UTC in summer the UK is already on the next day, and a deadline
 * printed with UTC's day and London's time would name an hour that never
 * happens. Returns '' for an empty/invalid input.
 */
export function formatLondonDateTime(iso: string | undefined | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const day = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/London',
  }).format(date);
  return `${day}, ${formatLondonTime(iso)}`;
}

/**
 * Join a nullable first/last name into a display string, e.g.
 * `{firstName:'Ada', lastName:'Lovelace'}` → "Ada Lovelace". Each part is
 * trimmed and empty/whitespace-only parts are dropped, so a partial name
 * renders cleanly ("Ada" or "Lovelace") and a fully-empty identity returns
 * `null` — NEVER "null null"/"undefined". Callers render their own fallback
 * (e.g. "Not recorded") on a null return. Used for the clinician review patient
 * name resolved from the step-0 Lead (AssessmentReview.patientIdentity).
 */
export function formatPatientName(
  identity: { firstName: string | null; lastName: string | null } | null | undefined,
): string | null {
  if (!identity) return null;
  const joined = [identity.firstName, identity.lastName]
    .map((part) => part?.trim() ?? '')
    .filter((part) => part.length > 0)
    .join(' ');
  return joined.length > 0 ? joined : null;
}

/**
 * The order reference to SHOW a human — the `ORD-…` display reference when the
 * server has one, else the `orderCaseId` UUID.
 *
 * WHY a fallback rather than the reference alone: the reference column arrived
 * with orders #105, so a case created before it (or served by an orders revision
 * that predates it) carries `null`/`undefined`. Staff must still see *something*
 * they can quote, so the id is the last resort — never an empty cell.
 */
export function orderDisplayRef(order: { reference?: string | null; orderCaseId: string }): string {
  return order.reference?.trim() || order.orderCaseId;
}
